/* eslint-disable @typescript-eslint/no-unused-vars */
import styles from './index.module.scss';
import { CommonCard } from '../../../../components/card';
import getFormatMessage from '../../../../locales/get-format-message';
import { ConsoleForm } from '../../../../components/form';
import { Form, Input, Radio, Space } from 'antd';
import { ReactNode, useEffect, useState } from 'react';

import {
  CancelButton,
  CommonButton,
  CommonButtonGroup,
  SubmitButton,
} from '../../../../components/button/main';

import { useHistory } from 'react-router-dom';
import { VDitorInput } from '../../components/vditor-input';
import { CreateNoticeNav } from '../create/nav-head';
import { ScopeSelector } from '../create/scope-selector';
import { approvalNotice, getNoticeDetail } from '../../../../api';
import {
  IApiNoticeItem,
  NoticeSystemEnable,
  NoticeSystemStatus,
} from '../../../../model/api-modules';
import { useForm } from 'antd/lib/form/Form';
import { messageError, messageSuccess } from '../../../../components/message';
import { hasAuthPerm, renderStatusTag } from '../lib';
import classNames from 'classnames';
import { getPdfFile } from '../lib/pdf';
import { getNoticeFileUrl, putNoticeObject } from '../lib/upload';
import { useSelector } from 'react-redux';
import { IRootState } from '../../../../store/reducers';

export enum PageMode {
  Detail = 'detail',
  Auth = 'auth',
  Update = 'update',
}

interface IProps {
  id: string;
  mode: PageMode;
}

interface IAuthFormInfo {
  toStatus: NoticeSystemStatus;
  reasonReject: string;
}

export const NoticeDetail = (props: IProps) => {
  const { id, mode } = props;
  const history = useHistory();
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);
  const [noticeDetail, setNoticeDetail] = useState<IApiNoticeItem>();
  const [form] = useForm<IAuthFormInfo>();
  const [formValues, setFormValues] = useState<IAuthFormInfo>();
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    const res = await getNoticeDetail(id);
    if (!res) {
      return;
    }
    setNoticeDetail(res);
  };

  const uploadPdf = async () => {
    if (!noticeDetail) {
      return '';
    }
    const fileInfo = await getPdfFile(
      document.querySelector(`.${styles.mailPreview}`) as HTMLElement,
      noticeDetail.id
    );
    await putNoticeObject(fileInfo.fileName, fileInfo.file);
    // console.log('pdf address:', await getNoticePdfFileUrl(fileInfo.fileName));
    return fileInfo.fileName;
  };

  const auth = async () => {
    const formInfo = await form.validateFields();
    formInfo.reasonReject = `${(userInfo?.UserID || '').split('@')[0]}: ${
      formInfo.reasonReject
    }`;
    setSubmitting(true);
    let mailContentPDFLink = '';
    if (
      formInfo.toStatus === NoticeSystemStatus.PendingPublic &&
      noticeDetail?.MyOAEnable === NoticeSystemEnable.Enable
    ) {
      mailContentPDFLink = await uploadPdf();
    }
    const res = await approvalNotice(id, { ...formInfo, mailContentPDFLink });
    setSubmitting(false);
    if (!res) {
      messageError(getFormatMessage('审核失败'));
      return;
    }
    messageSuccess(getFormatMessage('审核成功'));
    goBack();
  };

  const publish = async () => {
    setSubmitting(true);
    const res = await approvalNotice(id, {
      toStatus: NoticeSystemStatus.Published,
    });
    setSubmitting(false);
    if (!res) {
      messageError(getFormatMessage('发布失败'));
      return;
    }
    messageSuccess(getFormatMessage('发布成功'));
    goBack();
  };

  const goBack = () => {
    history.push(`/admin/notice/list${location.search}`);
  };

  if (!noticeDetail) {
    return null;
  }

  const renderAuthForm = () => {
    if (mode === PageMode.Detail) {
      return null;
    }
    if (!hasAuthPerm()) {
      return null;
    }
    if (noticeDetail.Status !== NoticeSystemStatus.PendingApproval) {
      return null;
    }
    return (
      <CommonCard title={getFormatMessage('通知审核')}>
        <ConsoleForm
          form={form}
          wrapperCol={{ span: 12 }}
          onFieldsChange={() => {
            setFormValues(form.getFieldsValue());
          }}
        >
          <Form.Item
            label={getFormatMessage('审核结果')}
            name="toStatus"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Radio.Group>
              <Space direction="vertical">
                <Radio value={NoticeSystemStatus.PendingPublic}>
                  {getFormatMessage('通过')}
                </Radio>
                <Radio value={NoticeSystemStatus.PendingUpdate}>
                  {getFormatMessage('不通过')}
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          {formValues?.toStatus === NoticeSystemStatus.PendingUpdate && (
            <Form.Item
              label={getFormatMessage('修改意见')}
              name="reasonReject"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input.TextArea placeholder={getFormatMessage('请输入')} />
            </Form.Item>
          )}
        </ConsoleForm>

        <CommonButtonGroup>
          <SubmitButton onClick={() => auth()} loading={submitting}>
            {getFormatMessage('submit')}
          </SubmitButton>
          <CancelButton onClick={goBack}>
            {getFormatMessage('cancel')}
          </CancelButton>
        </CommonButtonGroup>
      </CommonCard>
    );
  };

  const renderTitle = () => {
    return (
      <span>
        {getFormatMessage('notice_title')}
        {renderStatusTag(noticeDetail.Status)}
      </span>
    );
  };

  const renderPublish = () => {
    if (mode === PageMode.Detail) {
      return null;
    }
    if (noticeDetail.Status !== NoticeSystemStatus.PendingPublic) {
      return null;
    }
    const isCreator = userInfo?.UserID === noticeDetail.Founder;

    return (
      <CommonButtonGroup>
        <SubmitButton
          onClick={publish}
          loading={submitting}
          disabled={!isCreator}
        >
          {getFormatMessage('notice_page_publish')}
        </SubmitButton>
        <CancelButton onClick={goBack}>
          {getFormatMessage('cancel')}
        </CancelButton>
      </CommonButtonGroup>
    );
  };

  return (
    <div className={styles.page}>
      <CreateNoticeNav
        currentTitle={
          noticeDetail.Status === NoticeSystemStatus.PendingPublic
            ? getFormatMessage('notice_publish_title')
            : getFormatMessage('notice_detail')
        }
      />
      <CommonCard title={renderTitle()}>
        <ConsoleForm className={styles.form}>
          <Form.Item label={getFormatMessage('notice_method')}>
            <span>{getFormatMessage('notice_email')}</span>
          </Form.Item>
          <Form.Item label={getFormatMessage('notice_subject')}>
            <span>{noticeDetail?.Title}</span>
          </Form.Item>
          <Form.Item
            label={getFormatMessage('notice_scope')}
            name="scope"
            initialValue={
              noticeDetail && {
                condition: noticeDetail.ScopeCondition,
                result: noticeDetail.ScopeResult,
                notSelected: noticeDetail.ScopeResultNotSelected,
              }
            }
          >
            <ScopeSelector readOnly={true} />
          </Form.Item>

          <Form.Item
            label={getFormatMessage('notice_content')}
            name="mailContent"
          >
            <div
              className={classNames(styles.mailPreview)}
              dangerouslySetInnerHTML={{
                __html: noticeDetail?.MailContent || '',
              }}
            ></div>
          </Form.Item>

          <Form.Item
            label={getFormatMessage('notice_myoa_toggle')}
            name="myOAEnable"
            extra={getFormatMessage('notice_myoa_reminder_desc')}
          >
            <span>
              {noticeDetail?.MyOAEnable
                ? getFormatMessage('opened')
                : getFormatMessage('notice_status_off')}
            </span>
          </Form.Item>
        </ConsoleForm>
      </CommonCard>

      {renderAuthForm()}

      {renderPublish()}
    </div>
  );
};
